import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, CircularProgress, FormLabel, RadioGroup } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { type Dayjs } from 'dayjs'

import { apiZodios } from '~api/apiZodios'
import { RelatedContactChip } from '~components/common'
import {
  Button,
  Icon,
  Radio,
  TagDropdown,
  TextField,
  Toast,
  Typography,
} from '~components/internal'
import ContactBookSelector from '~components/internal/ContactBookSelector'
import RichTextEditorComponent from '~components/internal/RichTextEditor'
import AddressInputTable from '~pages/ContactsScreen/components/AddressInputTable/AddressInputTable'
import AttributionInputTable from '~pages/ContactsScreen/components/AttributionInputTable/AttributionInputTable'
import EmailInputTable from '~pages/ContactsScreen/components/EmailInputTable/EmailInputTable'
import PhoneInputTable from '~pages/ContactsScreen/components/PhoneInputTable/PhoneInputTable'
import { type RootState } from '~redux/rootReducer'
import { accentColors, typography } from '~tokens/internal'
import {
  type Address0,
  type AttributionCreate,
  type ContactEmail0,
  type ContactEntry2,
  type ContactEntryId,
  type EmailIdentity,
  type IdentityId,
  type PhoneNumber0,
  type RelatedContactEntry2,
  type TagData,
  type TargetId,
} from '~types'

import AddRelatedContact from '../AddRelatedContactForm/AddRelatedContactForm'

import './AddContactDialog.scss'
import useAddContactFormValidation from './AddContactFormValidation'

type RelatedContactWithReverse = RelatedContactEntry2 & {
  createReverse?: boolean
  reverseName?: string
}

interface AddContactFormControlProps {
  onSaveSuccess?: (
    contactEntryId: ContactEntryId,
    identityId: IdentityId,
    targetId: TargetId,
  ) => void
  onSaveError?: (error: Error) => void
  onClose?: () => void
  onComplete?: () => void
  initialData?: ContactFormData
  update?: boolean
  onUpdate?: (formData: ContactFormData) => Promise<void>
  inputOrganization?: EmailIdentity | null
  isSaved?: boolean
  setIsSaved?: (isSaved: boolean) => void
  onFormValidChange?: (isValid: boolean) => void
}

export interface ContactFormData {
  title: string
  firstName: string
  middleName: string
  lastName: string
  suffix: string
  dateOfBirth: Dayjs
  emails: ContactEmail0[]
  phoneNumbers: PhoneNumber0[]
  addresses: Address0[]
  attributions: AttributionCreate[]
  tags: TagData[]
  related_contacts?: RelatedContactWithReverse[]
  is_organization: boolean
  notes: string
}

const AddContactFormControl: React.FC<AddContactFormControlProps> = ({
  onSaveSuccess,
  onSaveError,
  onClose,
  onComplete,
  initialData,
  update = false,
  onUpdate,
  inputOrganization,
  isSaved = false,
  setIsSaved,
  onFormValidChange,
}) => {
  const user = useSelector((state: RootState) => state?.auth.user)
  const defaultOrganization = useSelector(
    (state: RootState) => state?.organization.selectedOrganization,
  )
  const [organization, setOrganization] = useState<EmailIdentity | null>(
    inputOrganization || defaultOrganization || null,
  )
  const [title, setTitle] = useState<string>(initialData?.title || '')
  const [firstName, setFirstName] = useState<string>(initialData?.firstName || '')
  const [middleName, setMiddleName] = useState<string>(initialData?.middleName || '')
  const [lastName, setLastName] = useState<string>(initialData?.lastName || '')
  const [suffix, setSuffix] = useState<string>(initialData?.suffix || '')
  const [dateOfBirth, setDateOfBirth] = useState<Dayjs | null>(initialData?.dateOfBirth || null)
  const [emails, setEmails] = useState<ContactEmail0[]>(initialData?.emails || [])
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber0[]>(initialData?.phoneNumbers || [])
  const [addresses, setAddresses] = useState<Address0[]>(initialData?.addresses || [])
  const [tags, setTags] = useState<TagData[]>(initialData?.tags || [])
  const [attributions, setAttributions] = useState<AttributionCreate[]>(
    initialData?.attributions || [],
  )

  const [relatedContacts, setRelatedContacts] = useState<RelatedContactWithReverse[]>(
    initialData?.related_contacts || [],
  )
  const [notes, setNotes] = useState<string>(initialData?.notes || '')
  const [isAddingRelatedContact, setIsAddingRelatedContact] = useState<boolean>(
    !initialData?.related_contacts || initialData.related_contacts.length === 0,
  )
  const [isAddingContact, setIsAddingContact] = useState<boolean>(false)
  const [isEmailsValid, setIsEmailsValid] = useState<boolean>(true)
  const [isPhonesValid, setIsPhonesValid] = useState<boolean>(true)
  const [isAttributesValid, setIsAttributesValid] = useState<boolean>(true)
  const [isOrganization, setIsOrganization] = useState<boolean>(
    initialData?.is_organization || false,
  )
  const [toast, setToast] = useState<{
    open: boolean
    message: string
    type: 'error' | 'warning'
  }>({
    open: false,
    message: '',
    type: 'error',
  })
  const [existingContact, setExistingContact] = useState<{
    name: string
    handle: string | null
  }>({
    name: '',
    handle: null,
  })

  const { validationState, isBasicInfoValid, handleBlur, shouldShowError, resetValidation } =
    useAddContactFormValidation(firstName, lastName, isOrganization)

  const isFormInvalid = !isBasicInfoValid || !isEmailsValid || !isPhonesValid || !isAttributesValid

  const handleAddRelatedContact = (
    contact: ContactEntry2,
    relationship: string,
    createReverse: boolean,
    reverseName: string,
    _deleteReverse?: boolean,
  ): void => {
    const relatedContact: RelatedContactWithReverse = {
      ...contact,
      relation: relationship,
      createReverse,
      reverseName,
    }
    if (relatedContacts.find((rc) => rc.id === relatedContact.id)) {
      setExistingContact({ name: '', handle: null })
      setToast({
        open: true,
        message: `Cannot add contact ${relatedContact.name} as it is already a relation of ${contact.name}.`,
        type: 'warning',
      })
      return
    }
    setRelatedContacts([...relatedContacts, relatedContact])
  }

  const handleSaveContact = async (): Promise<void> => {
    try {
      if (!user) return
      if (!organization) return
      if (isAddingContact) return

      setToast({ open: false, message: '', type: 'error' })
      setIsAddingContact(true)

      if (update && onUpdate) {
        const formData: ContactFormData = {
          title,
          firstName,
          middleName,
          lastName,
          suffix,
          dateOfBirth: dateOfBirth as Dayjs,
          emails,
          phoneNumbers,
          addresses,
          attributions,
          tags,
          related_contacts: relatedContacts,
          is_organization: isOrganization,
          notes,
        }
        await onUpdate(formData)
        setIsAddingContact(false)
        onComplete?.()
        handleClose()
        return
      }

      const response = await apiZodios.contacts_createContact({
        user_id: user.id,
        organization_identity_id: organization.id,
        is_organization: isOrganization,
        first_name: firstName,
        middle_name: isOrganization ? '' : middleName,
        last_name: isOrganization ? '' : lastName,
        suffix: isOrganization ? '' : suffix,
        date_of_birth: isOrganization ? null : dateOfBirth?.format('YYYY-MM-DD'),
        emails: emails.map((email) => email.address),
        phones: phoneNumbers.map((phone) => phone.phone_number),
        addresses: addresses.map((address) => address.id),
        tags: tags.map((tag) => tag.id),
        attributions: attributions.map((a) => ({
          attribute_id: null,
          name: a.name,
          value: a.value,
        })),
      })
      await Promise.all(
        relatedContacts.map(async (relatedContact) => {
          if (!response.contact_entry_id) return
          await apiZodios.contacts_createRelatedContact({
            user_id: user.id,
            organization_identity_id: organization.id,
            from_contact_entry_id: response.contact_entry_id,
            to_contact_entry_id: relatedContact.id,
            name: relatedContact.relation,
            create_reverse: relatedContact.createReverse || false,
            reverse_name: relatedContact.reverseName || '',
          })
        }),
      )
      setIsAddingContact(false)
      setExistingContact({
        name: response.existing_contact_name || '',
        handle: response.existing_contact_handle ?? null,
      })

      if (response.email_address_conflict) {
        setToast({
          open: true,
          message: 'A contact with this email address already exists.',
          type: 'warning',
        })
        setIsAddingContact(false)
        setIsSaved?.(false)
        return
      }

      onSaveSuccess?.(
        response.contact_entry_id as ContactEntryId,
        response.identity_id as IdentityId,
        response.target_id as TargetId,
      )
      onComplete?.()
      handleClose()
    } catch (error) {
      onSaveError?.(error as Error)
      setExistingContact({ name: '', handle: null })
      setToast({
        open: true,
        message: 'An error occurred while saving the contact.',
        type: 'error',
      })
      setIsSaved?.(false)
      setIsAddingContact(false)
    }
  }

  const handleClose = (): void => {
    setTitle('')
    setFirstName('')
    setMiddleName('')
    setLastName('')
    setSuffix('')
    setDateOfBirth(null)
    setEmails([])
    setPhoneNumbers([])
    setAddresses([])
    setTags([])
    setAttributions([])
    setNotes('')
    setToast({ open: false, message: '', type: 'error' })
    setIsSaved?.(false)
    resetValidation()
    onClose?.()
  }

  useEffect(() => {
    onFormValidChange?.(isFormInvalid)
  }, [isFormInvalid])

  useEffect(() => {
    if (isSaved && !isFormInvalid) {
      handleSaveContact()
    }
  }, [isSaved, isFormInvalid])

  return (
    <Box component='form'>
      {isAddingContact && (
        <Box className='add-contact-dialog__overlay'>
          <CircularProgress size={typography.fontSize.mega} />
          <Typography className='add-contact-dialog__overlay-text'>
            {update ? 'Updating contact...' : 'Adding contact...'}
          </Typography>
        </Box>
      )}
      <Box className='add-contact-dialog__form'>
        {!inputOrganization ? (
          <ContactBookSelector onSelect={setOrganization} value={organization?.id} />
        ) : (
          <Typography variant='subtitle1'>{organization?.name}</Typography>
        )}

        <Box className='add-contact-dialog__type-selector'>
          <FormLabel>Type of Contact</FormLabel>
          <RadioGroup
            row
            value={isOrganization}
            onChange={(e) => setIsOrganization(e.target.value === 'true')}
          >
            <Radio label='Individual' value={false} />
            <Radio label='Organization' value={true} />
          </RadioGroup>
        </Box>

        {/* Basic contact information fields */}
        <Box className='add-contact-dialog__fields'>
          {!isOrganization && (
            <Box className='add-contact-dialog__field'>
              <TextField label='Title' value={title} onChange={(e) => setTitle(e.target.value)} />
            </Box>
          )}
          <Box className='add-contact-dialog__field'>
            <TextField
              label={isOrganization ? 'Name' : 'First Name'}
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={() => handleBlur('firstName')}
              error={shouldShowError('firstName')}
              errorText={validationState.firstName.error}
            />
          </Box>
          {!isOrganization && (
            <>
              <Box className='add-contact-dialog__field'>
                <TextField
                  label='Middle Name'
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                />
              </Box>
              <Box className='add-contact-dialog__field'>
                <TextField
                  label='Last Name'
                  required={!isOrganization}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onBlur={() => handleBlur('lastName')}
                  error={shouldShowError('lastName')}
                  errorText={validationState.lastName.error}
                />
              </Box>
              <Box className='add-contact-dialog__field'>
                <TextField
                  label='Suffix'
                  value={suffix}
                  onChange={(e) => setSuffix(e.target.value)}
                />
              </Box>
            </>
          )}
        </Box>

        {!isOrganization && (
          <Box className='add-contact-dialog__field'>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label='Date of Birth'
                value={dateOfBirth}
                onChange={(newValue) => setDateOfBirth(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        )}

        {/* Contact information tables */}
        <EmailInputTable
          initialEmails={initialData?.emails}
          setEmails={(newEmails) => {
            setEmails(newEmails)
          }}
          onValidationChange={setIsEmailsValid}
        />
        <PhoneInputTable
          initialPhoneNumbers={initialData?.phoneNumbers}
          setPhoneNumbers={setPhoneNumbers}
          onValidationChange={setIsPhonesValid}
        />
        <AddressInputTable
          addresses={addresses}
          setAddresses={setAddresses}
          organizationId={organization?.id as IdentityId | undefined}
        />
        <AttributionInputTable
          initialAttributions={initialData?.attributions}
          setAttributions={setAttributions}
          onValidationChange={setIsAttributesValid}
        />
        {/* Tags section */}
        <Box className='add-contact-dialog__field'>
          <TagDropdown
            label='Tags'
            selectedTags={tags}
            onChange={(selectedTags) => setTags(selectedTags)}
            selectedIdentityId={organization?.id}
          />
        </Box>

        {/* Related contacts section */}
        <Box>
          <FormLabel>Related Contacts</FormLabel>
          <Box className='add-contact-dialog__related-contacts'>
            {relatedContacts.length > 0 && (
              <Box>
                {relatedContacts.map((relatedContact) => (
                  <RelatedContactChip
                    key={relatedContact.id}
                    contact={relatedContact}
                    onDelete={() =>
                      setRelatedContacts(
                        relatedContacts.filter((contact) => contact.id !== relatedContact.id),
                      )
                    }
                  />
                ))}
              </Box>
            )}
            {isAddingRelatedContact ? (
              <AddRelatedContact
                handleAddRelatedContact={(...args) => {
                  handleAddRelatedContact(...args)
                  setIsAddingRelatedContact(false)
                }}
              />
            ) : (
              <Button
                variant='text'
                size='small'
                width='fit'
                startIcon={<Icon name='AddCircleOutlined' />}
                onClick={() => setIsAddingRelatedContact(true)}
              >
                Add another
              </Button>
            )}
          </Box>
        </Box>

        {/* Notes section */}
        {update && (
          <Box className='add-contact-dialog__field'>
            <FormLabel>Notes</FormLabel>
            <RichTextEditorComponent
              className='add-contact-notes-editor'
              initialContent={notes}
              onChange={(htmlContent) => setNotes(htmlContent)}
            />
          </Box>
        )}
      </Box>

      <Toast
        type={toast.type}
        message={toast.message}
        open={toast.open}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        action={
          organization?.handle && existingContact.handle ? (
            <Button
              variant='text'
              startIcon={<Icon name='Person' />}
              sx={{
                gap: 0,
                color: `${accentColors.neutral.darker} !important`,
                backgroundColor: `${accentColors.neutral.lighter} !important`,
                '&:hover': { backgroundColor: `${accentColors.neutral.light} !important` },
              }}
              onClick={() =>
                window.open(
                  `/organizations/${organization?.handle}/contacts/${existingContact.handle}`,
                  '_blank',
                )
              }
            >
              {existingContact.name || 'View Contact'}
            </Button>
          ) : undefined
        }
      />
    </Box>
  )
}

export default AddContactFormControl
